import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Switch,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import i18n from "src/i18n";
import { z } from "zod";

import { CustomFormBuilder } from "@components/CustomFormBuilder";

import { getConsultationServiceCategoryValues } from "@data/dropdownValues";

import { useUpdateClinicConsultationService } from "@hooks/mutations/clinic/useUpdateClinicConsultationService";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";

import { ClinicConsultationService } from "@customTypes/clinic";

import { StyledEditConsultationServiceModalContent } from "./EditConsultationServiceModal.styled";


const editFormSchema = () =>
  z.object({
    name: z.string().min(1, i18n.t("name-required")),
    category: z.string().optional(),
    price: z.number().min(1, i18n.t("price-required")),
    status: z.string().min(1, i18n.t("status-required")),
    partner_id: z.string().optional(),
    service_duration: z.number().min(1, i18n.t("duration-required")).optional(),
    in_30_min: z.boolean().optional(),
    sessions: z.number().int().min(1, i18n.t("sessions-required")),
    session_interval_days: z
      .number()
      .int()
      .min(1, i18n.t("session-interval-days-required")),
  });

type EditFormInputs = z.infer<ReturnType<typeof editFormSchema>>;

interface EditConsultationServiceModalProps {
  opened: boolean;
  close: () => void;
  consultationService: ClinicConsultationService | null;
}

export const EditConsultationServiceModal = ({
  opened,
  close,
  consultationService,
}: EditConsultationServiceModalProps) => {
  const { t } = useTranslation();

  const {
    mutate: updateConsultationService,
    isLoading: isUpdateConsultationServiceLoading,
  } = useUpdateClinicConsultationService();

  const { data: partnersData } = useGetPartners({ isActive: true });

  const partnersOptions = useMemo(() => {
    return (partnersData || []).map(partner => ({
      label: partner.name,
      value: partner._id,
    }));
  }, [partnersData]);

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(editFormSchema()),
    initialValues: {
      name: "",
      category: "",
      price: 0,
      status: "active",
      partner_id: "",
      service_duration: 30,
      in_30_min: false,
      sessions: 1,
      session_interval_days: 21,
    },
  });

  // Update form values when consultationService changes
  useEffect(() => {
    if (consultationService && opened) {
      form.initialize({
        name: consultationService.name || "",
        category: consultationService.category || "",
        price: consultationService.price || 0,
        status: consultationService.status || "active",
        partner_id: consultationService.partner_id || "",
        service_duration: consultationService.service_duration || 30,
        in_30_min: consultationService.in_30_min || false,
        sessions: consultationService.sessions ?? 1,
        session_interval_days: consultationService.session_interval_days ?? 21,
      });
    }
  }, [consultationService, opened]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!opened) {
      form.reset();
    }
  }, [opened]);

  const handleFormSubmit = (values: EditFormInputs) => {
    if (!consultationService?.id) return;

    updateConsultationService(
      {
        consultationServiceId: consultationService.id,
        consultationService: values,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("edit-consultation-service")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="lg">
      <Tabs defaultValue="details">
        <Tabs.List>
          <Tabs.Tab value="details">{t("details")}</Tabs.Tab>
          <Tabs.Tab value="form">{t("pre-appointment-form")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details">
          <StyledEditConsultationServiceModalContent>
            <form
              className="form"
              onSubmit={form.onSubmit(
                values => handleFormSubmit(values),
                errors => {
                  const firstErrorPath = Object.keys(errors)[0];
                  form.getInputNode(firstErrorPath)?.focus();
                }
              )}>
              <div className="form-field">
                <TextInput
                  label={t("name")}
                  withAsterisk
                  {...form.getInputProps("name")}
                  key={form.key("name")}
                  placeholder={t("eg", { example: "Homecare" })}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("category")}
                  placeholder={t("select-category")}
                  data={getConsultationServiceCategoryValues()}
                  {...form.getInputProps("category")}
                  key={form.key("category")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("partner")}
                  placeholder={t("select-partner")}
                  data={partnersOptions}
                  searchable
                  clearable
                  {...form.getInputProps("partner_id")}
                  key={form.key("partner_id")}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("price")}
                  withAsterisk
                  {...form.getInputProps("price")}
                  key={form.key("price")}
                  min={0}
                  step={1}
                  placeholder="30000"
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("service-duration-minutes", "Service Duration (Minutes)")}
                  {...form.getInputProps("service_duration")}
                  key={form.key("service_duration")}
                  min={5}
                  step={5}
                  placeholder="30"
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("consultation-service-sessions")}
                  description={t("consultation-service-sessions-description")}
                  withAsterisk
                  min={1}
                  step={1}
                  allowDecimal={false}
                  {...form.getInputProps("sessions")}
                  key={form.key("sessions")}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("session-interval-days")}
                  description={t("session-interval-days-description")}
                  withAsterisk
                  min={1}
                  step={1}
                  allowDecimal={false}
                  {...form.getInputProps("session_interval_days")}
                  key={form.key("session_interval_days")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("status")}
                  withAsterisk
                  placeholder={t("select-status")}
                  data={[
                    { label: t("active"), value: "active" },
                    { label: t("inactive"), value: "inactive" },
                  ]}
                  {...form.getInputProps("status")}
                  key={form.key("status")}
                />
              </div>

              <div className="form-field" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <Switch
                  label={t("in-30-min-available", "Available for ASAP (in 30 min) booking")}
                  {...form.getInputProps("in_30_min", { type: "checkbox" })}
                  key={form.key("in_30_min")}
                />
              </div>

              <div className="form-actions">
                <PermissionGate permission="resources.edit"><Button
                  type="submit"
                  loading={isUpdateConsultationServiceLoading}
                  fullWidth>
                  {t("update")}
                </Button></PermissionGate>
              </div>
            </form>
          </StyledEditConsultationServiceModalContent>
        </Tabs.Panel>

        <Tabs.Panel value="form">
          <div style={{ padding: "0 20px 20px" }}>
            {consultationService?.id && (
              <CustomFormBuilder serviceId={consultationService.id} />
            )}
          </div>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
};
